import React from "react";
import PropTypes from "prop-types";
import classNames from "../../../../utils/classNames";
import IconButton from "../../../atoms/IconButton";
import OnlineStatus from "../../../molecules/OnlineStatus";
import OnlineStatusExpanded from "../../../molecules/OnlineStatusExpanded";
import useUserPresence from "../../../../hooks/useUserPresence";

import "./index.scss";

export default function ChatConversationHeader({
    as: Root = "header",
    leading,
    title,
    titleAs,
    titleBadge,
    tags,
    secondaryItems,
    actions,
    className,
    presenceUserId,
    presenceStatus,
    presenceLastSeen,
    showPresenceLabel,
    presencePollInterval,
    presenceDisplay,
}) {
    const TitleTag = titleAs;
    const hasTags = Array.isArray(tags) && tags.length > 0;
    const hasSecondary = Array.isArray(secondaryItems) && secondaryItems.length > 0;
    const hasActions = Array.isArray(actions) && actions.length > 0;
    const shouldTrackPresence = Boolean(presenceUserId);

    const livePresence = useUserPresence(
        shouldTrackPresence ? presenceUserId : undefined,
        {
            enabled: shouldTrackPresence,
            pollInterval: presencePollInterval,
            initialStatus: presenceStatus,
            initialLastSeen: presenceLastSeen,
        },
    );

    const resolvedStatus = shouldTrackPresence ? livePresence.status : (presenceStatus ?? "unknown");
    const resolvedLastSeen = shouldTrackPresence ? livePresence.lastSeen : presenceLastSeen ?? null;
    const isOnline = resolvedStatus === "online";
    const isAway = resolvedStatus === "away";
    const hasPresence = Boolean(presenceUserId || presenceStatus);
    const resolvedPresenceDisplay = presenceDisplay ?? "compact";

    const renderPresence = () => {
        if (!hasPresence) {
            return null;
        }

        if (resolvedPresenceDisplay === "expanded") {
            return (
                <span className="chat-conversation__presence chat-conversation__presence--expanded">
                    <OnlineStatusExpanded online={isOnline} lastSeen={resolvedLastSeen} />
                </span>
            );
        }

        return (
            <span className="chat-conversation__presence">
                <OnlineStatus online={isOnline} lastSeen={resolvedLastSeen} />
                {showPresenceLabel ? (
                    <span className="chat-conversation__presence-label">
                        {isOnline ? "Online" : isAway ? "Away" : "Offline"}
                    </span>
                ) : null}
            </span>
        );
    };

    return (
        <Root className={classNames("chat-conversation__header", className)}>
            <div className="chat-conversation__identity">
                {leading || null}
                <div className="chat-conversation__details">
                    <div className="chat-conversation__primary">
                        <div className="chat-conversation__title">
                            {typeof title === "string" ? (
                                <TitleTag>
                                    {titleBadge ? (
                                        <span className="chat-conversation__title-name">{title}</span>
                                    ) : title}
                                    {titleBadge || null}
                                </TitleTag>
                            ) : title}
                            {renderPresence()}
                        </div>
                        {hasTags ? tags.map((tag) => (
                            <span
                                key={tag.id ?? tag.label}
                                className={classNames(
                                    "chat-conversation__tag",
                                    tag.variant && `chat-conversation__tag--${tag.variant}`,
                                    tag.className,
                                )}
                            >
                                {tag.icon ? (
                                    <span className="chat-conversation__tag-icon" aria-hidden="true">
                                        {tag.icon}
                                    </span>
                                ) : null}
                                <span>{tag.label}</span>
                            </span>
                        )) : null}
                    </div>
                    {hasSecondary ? (
                        <div className="chat-conversation__secondary">
                            {secondaryItems.map((item) => (
                                <span
                                    key={item.id ?? item.label}
                                    className="chat-conversation__secondary-item"
                                >
                                    {item.icon ? (
                                        <span className="chat-conversation__secondary-icon" aria-hidden="true">
                                            {item.icon}
                                        </span>
                                    ) : null}
                                    <span className="chat-conversation__secondary-label">{item.label}</span>
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            {hasActions ? (
                <div className="chat-conversation__actions">
                    {actions.map((action) => {
                        if (action?.render) {
                            return (
                                <React.Fragment key={action.id ?? action.label}>
                                    {action.render}
                                </React.Fragment>
                            );
                        }
                        return (
                            <IconButton
                                key={action.id ?? action.label}
                                variant={action.variant ?? "ghost"}
                                label={action.label}
                                icon={action.icon}
                                onClick={action.onClick}
                                size={action.size}
                                disabled={action.disabled}
                                className={action.className}
                                type={action.type}
                            />
                        );
                    })}
                </div>
            ) : null}
        </Root>
    );
}

ChatConversationHeader.propTypes = {
    as: PropTypes.elementType,
    leading: PropTypes.node,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    titleAs: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6"]),
    titleBadge: PropTypes.node,
    tags: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        icon: PropTypes.node,
        variant: PropTypes.string,
        className: PropTypes.string,
    })),
    secondaryItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        icon: PropTypes.node,
    })),
    presenceUserId: PropTypes.string,
    presenceStatus: PropTypes.oneOf(["online", "offline", "away", "unknown"]),
    presenceLastSeen: PropTypes.string,
    showPresenceLabel: PropTypes.bool,
    presencePollInterval: PropTypes.number,
    presenceDisplay: PropTypes.oneOf(["compact", "expanded"]),
    actions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string.isRequired,
        icon: PropTypes.node,
        onClick: PropTypes.func,
        variant: PropTypes.oneOf(["confirm", "ghost"]),
        size: PropTypes.number,
        disabled: PropTypes.bool,
        className: PropTypes.string,
        type: PropTypes.oneOf(["button", "submit", "reset"]),
        render: PropTypes.node,
    })),
    className: PropTypes.string,
};

ChatConversationHeader.defaultProps = {
    as: "header",
    leading: null,
    titleAs: "h2",
    titleBadge: null,
    tags: [],
    secondaryItems: [],
    presenceUserId: undefined,
    presenceStatus: undefined,
    presenceLastSeen: null,
    showPresenceLabel: false,
    presencePollInterval: 45000,
    presenceDisplay: "compact",
    actions: [],
    className: undefined,
};
